import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChakraProvider, useMediaQuery } from '@chakra-ui/react';
import { MdMenu, MdDashboard, MdTrendingUp, MdBarChart, MdPerson, MdHistory, MdSettings, MdLocalActivity, MdAutorenew, MdArrowBack, MdFace, MdEventAvailable, MdForum, MdKeyboardArrowLeft, MdFavorite, MdChat, MdShare, MdBookmark, MdVisibility } from 'react-icons/md';
import { RiCopperCoinLine } from 'react-icons/ri';
import { FaRobot } from 'react-icons/fa';
import translations from '../utils/translations.json';
import './Analytics.css';

const Analytics = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobile] = useMediaQuery('(max-width: 768px)');
    const [language, setLanguage] = useState(localStorage.getItem('language') || 'pt');
    const [analyticsData, setAnalyticsData] = useState(null);
    const [trendsData, setTrendsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('all');
    const [activeTab, setActiveTab] = useState('overview');
    
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const userId = localStorage.getItem('userId');
    
    const fromGrowthPilot = params.get('from') === 'growthpilot' || location.pathname.includes('/growthpilot/');

    function totenIdToIndex(totenId) {
        if (!totenId) {
            console.warn('totenId não encontrado no localStorage, usando índice 0');
            return 0;
        }
        const suffix = totenId.replace('perfil', '');
        const index = parseInt(suffix, 10) - 1;
        return index >= 0 ? index : 0;
    }

    useEffect(() => {
        if (userId) {
            fetchAnalyticsData();
        }
    }, [userId, selectedPeriod]);

    const fetchAnalyticsData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const totenId = localStorage.getItem('totenId');
            const totenIndex = totenIdToIndex(totenId);
            
            console.log('Debug Analytics:', { totenId, totenIndex, userId });
            
            // Buscar dados principais
            const analyticsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/analytics/${userId}?totenIndex=${totenIndex}&period=${selectedPeriod}`, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('Analytics Response Status:', analyticsResponse.status);
            
            if (!analyticsResponse.ok) {
                const errorText = await analyticsResponse.text();
                console.error('Analytics Error Response:', errorText);
                throw new Error(`Erro ao buscar dados de analytics: ${analyticsResponse.status}`);
            }
            
            const analyticsResult = await analyticsResponse.json();
            console.log('Analytics Result:', analyticsResult);
            
            if (analyticsResult.success && analyticsResult.analytics) {
                setAnalyticsData(analyticsResult.analytics.metrics);
            } else {
                console.error('Analytics data structure:', analyticsResult);
                throw new Error('Dados de analytics não encontrados');
            }
            
            // Buscar dados de tendências
            const trendsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/analytics/${userId}/trends?totenIndex=${totenIndex}&period=${selectedPeriod}`, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
            
            if (trendsResponse.ok) {
                const trendsResult = await trendsResponse.json();
                console.log('Trends Result:', trendsResult);
                if (trendsResult.success && trendsResult.trends) {
                    setTrendsData(trendsResult.trends);
                }
            }
            
        } catch (err) {
            console.error('Erro ao buscar dados:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSidebarToggle = () => {
        if (isMobile) {
            setIsSidebarOpen(prev => !prev);
        } else {
            setIsSidebarCollapsed(prev => !prev);
        }
    };

    const automacaoOpen = (language) => {
        if (fromGrowthPilot) {
            navigate(`/automacao?lang=${language}&from=growthpilot`);
        } else {
            navigate(`/automation?lang=${language}`);
        }
    }

    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toString();
    };

    const formatPercentage = (num) => {
        return num ? num.toFixed(2) + '%' : '0%';
    };

    // Sidebar igual ao GrowthPilot
    const renderSidebar = () => {
        if (!fromGrowthPilot) return null;
        return (
            <div className={`sidebar-fixed ${isMobile && isSidebarOpen ? 'active' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <FaRobot size={22} />
                        <span className="menu-text">GrowthPilot</span>
                    </div>
                    <div className="sidebar-toggle-btn" onClick={handleSidebarToggle}>
                        <MdKeyboardArrowLeft size={24} />
                    </div>
                </div>
                <div className="sidebar-menu">
                    <div className="menu-item" onClick={() => navigate(`/growthpilot?lang=${language}`)}>
                        <MdEventAvailable size={20} />
                        <span className="menu-text">Content Planner</span>
                    </div>
                    <div className="menu-item" onClick={() => navigate(`/pci?from=growthpilot&lang=${language}`)}>
                        <RiCopperCoinLine size={20} />
                        <span className="menu-text">PCI</span>
                    </div>
                    <div className="menu-item" onClick={() => navigate(`/viewuser?lang=${language}&from=growthpilot`)}>
                        <MdFace size={20} />
                        <span className="menu-text">Avatares</span>
                    </div>
                    <div className="menu-item" onClick={() => navigate(`/history?lang=${language}&from=growthpilot`)}>
                        <MdForum size={20} />
                        <span className="menu-text">Histórico de Conteúdo</span>
                    </div>
                    <div className="menu-item" onClick={() => automacaoOpen(language)}>
                        <MdAutorenew size={20} />
                        <span className="menu-text">Automação</span>
                    </div>
                    <div className="menu-item active">
                        <MdDashboard size={20} />
                        <span className="menu-text">Analytics</span>
                    </div>
                    <div className="menu-item" onClick={() => navigate(`/growthpilot/config?lang=${language}`)}>
                        <MdSettings size={20} />
                        <span className="menu-text">Configurações</span>
                    </div>
                    <div className="menu-item" onClick={() => navigate(`/main?lang=${language}`)}>
                        <MdArrowBack size={20} />
                        <span className="menu-text">Voltar para o PostSucesso</span>
                    </div>
                </div>
                <div className="sidebar-footer">
                    <div className="support-button">
                        <MdLocalActivity size={22} />
                        <span className="menu-text">Suporte</span>
                    </div>
                </div>
            </div>
        );
    };

    const renderMetricsCard = (title, value, subtitle, icon, color = '#8E59FF') => (
        <div className="metrics-card" style={{ borderLeft: `4px solid ${color}` }}>
            <div className="metrics-icon" style={{ backgroundColor: color + '20' }}>
                {icon}
            </div>
            <div className="metrics-content">
                <h3>{title}</h3>
                <div className="metrics-value">{value}</div>
                <div className="metrics-subtitle">{subtitle}</div>
            </div>
        </div>
    );

    const renderPeriodSelector = () => (
        <div className="period-selector">
            <button 
                className={selectedPeriod === '7d' ? 'active' : ''} 
                onClick={() => setSelectedPeriod('7d')}
            >
                7 dias
            </button>
            <button 
                className={selectedPeriod === '30d' ? 'active' : ''} 
                onClick={() => setSelectedPeriod('30d')}
            >
                30 dias
            </button>
            <button 
                className={selectedPeriod === '90d' ? 'active' : ''} 
                onClick={() => setSelectedPeriod('90d')}
            >
                90 dias
            </button>
            <button 
                className={selectedPeriod === 'all' ? 'active' : ''} 
                onClick={() => setSelectedPeriod('all')}
            >
                Total
            </button>
        </div>
    );

    const renderDashboard = () => {
        if (loading) {
            return (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Carregando dados de analytics...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="error-container">
                    <h3>Erro ao carregar dados</h3>
                    <p>{error}</p>
                    <button onClick={fetchAnalyticsData}>Tentar novamente</button>
                </div>
            );
        }

        if (!analyticsData) {
            return (
                <div className="no-data-container">
                    <h3>Nenhum dado disponível</h3>
                    <p>Não foram encontrados dados de analytics para este período.</p>
                </div>
            );
        }

        return (
            <div className="analytics-dashboard">
                <div className="dashboard-header">
                    <h1>Analytics</h1>
                    <p>Métricas de desempenho dos seus posts</p>
                    {renderPeriodSelector()}
                </div>

                <div className="metrics-grid">
                    {renderMetricsCard(
                        'Total de Curtidas',
                        formatNumber(analyticsData.totalLikes || 0),
                        `${analyticsData.postsCount || 0} posts`,
                        <MdFavorite size={24} />,
                        '#FF6B6B'
                    )}
                    
                    {renderMetricsCard(
                        'Total de Comentários',
                        formatNumber(analyticsData.totalComments || 0),
                        'Engajamento',
                        <MdChat size={24} />,
                        '#4ECDC4'
                    )}
                    
                    {renderMetricsCard(
                        'Total de Compartilhamentos',
                        formatNumber(analyticsData.totalShares || 0),
                        'Alcance orgânico',
                        <MdShare size={24} />,
                        '#45B7D1'
                    )}
                    
                    {renderMetricsCard(
                        'Total de Salvamentos',
                        formatNumber(analyticsData.totalSaved || 0),
                        'Conteúdo valioso',
                        <MdBookmark size={24} />,
                        '#96CEB4'
                    )}
                    
                    {renderMetricsCard(
                        'Alcance Total',
                        formatNumber(analyticsData.totalReach || 0),
                        'Pessoas alcançadas',
                        <MdVisibility size={24} />,
                        '#FFEAA7'
                    )}
                    
                    {renderMetricsCard(
                        'Desempenho',
                        formatPercentage(analyticsData.performance || 0),
                        'Taxa de engajamento',
                        <MdTrendingUp size={24} />,
                        '#8E59FF'
                    )}
                </div>

                {trendsData && Object.keys(trendsData).length > 0 && (
                    <div className="trends-section">
                        <h2>Evolução do Desempenho</h2>
                        <div className="tabs-container">
                            <div className="tabs-header">
                                <button 
                                    className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('overview')}
                                >
                                    📊 Visão Geral
                                </button>
                                <button 
                                    className={`tab-button ${activeTab === 'charts' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('charts')}
                                >
                                    📈 Gráficos
                                </button>
                            </div>
                            
                            <div className="tab-content">
                                {activeTab === 'overview' && (
                                    <div className="trends-visual">
                                        <div className="trends-chart">
                                            {Object.entries(trendsData).map(([period, data], index) => (
                                                <div key={period} className="trend-bar">
                                                    <div className="bar-header">
                                                        <span className="period-label">
                                                            {period === '7d' ? 'Últimos 7 dias' : period === '30d' ? 'Últimos 30 dias' : period === '90d' ? 'Últimos 90 dias' : 'Total'}
                                                        </span>
                                                        <span className="performance-value">{formatPercentage(data.performance || 0)}</span>
                                                    </div>
                                                    <div className="bar-container">
                                                        <div 
                                                            className="performance-bar" 
                                                            style={{ 
                                                                width: `${Math.min((data.performance || 0) * 2, 100)}%`,
                                                                backgroundColor: index === 0 ? '#FF6B6B' : index === 1 ? '#4ECDC4' : index === 2 ? '#45B7D1' : '#8E59FF'
                                                            }}
                                                        ></div>
                                                    </div>
                                                    <div className="bar-metrics">
                                                        <div className="metric-pill">
                                                            <span className="pill-icon">❤️</span>
                                                            <span className="pill-value">{formatNumber(data.totalLikes || 0)}</span>
                                                        </div>
                                                        <div className="metric-pill">
                                                            <span className="pill-icon">💬</span>
                                                            <span className="pill-value">{formatNumber(data.totalComments || 0)}</span>
                                                        </div>
                                                        <div className="metric-pill">
                                                            <span className="pill-icon">📤</span>
                                                            <span className="pill-value">{formatNumber(data.totalShares || 0)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        
                                        <div className="trends-summary">
                                            <div className="summary-card">
                                                <div className="summary-icon">📈</div>
                                                <div className="summary-content">
                                                    <h4>Melhor Período</h4>
                                                    <p>{(() => {
                                                        const periods = Object.entries(trendsData);
                                                        const bestPeriod = periods.reduce((best, current) => 
                                                            (current[1].performance || 0) > (best[1].performance || 0) ? current : best
                                                        );
                                                        return bestPeriod[0] === '7d' ? '7 Dias' : 
                                                               bestPeriod[0] === '30d' ? '30 Dias' : 
                                                               bestPeriod[0] === '90d' ? '90 Dias' : 'Total';
                                                    })()}</p>
                                                </div>
                                            </div>
                                            
                                            <div className="summary-card">
                                                <div className="summary-icon">🎯</div>
                                                <div className="summary-content">
                                                    <h4>Engajamento Total</h4>
                                                    <p>{(() => {
                                                        const total = Object.values(trendsData).reduce((sum, data) => 
                                                            sum + (data.totalLikes || 0) + (data.totalComments || 0) + (data.totalShares || 0), 0
                                                        );
                                                        return formatNumber(total);
                                                    })()}</p>
                                                </div>
                                            </div>
                                            
                                            <div className="summary-card">
                                                <div className="summary-icon">👥</div>
                                                <div className="summary-content">
                                                    <h4>Alcance Total</h4>
                                                    <p>{(() => {
                                                        const maxReach = Math.max(...Object.values(trendsData).map(data => data.totalReach || 0));
                                                        return formatNumber(maxReach);
                                                    })()}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {activeTab === 'charts' && (
                                    <div className="charts-container">
                                        <div className="chart-grid">
                                            <div className="chart-card">
                                                <h3>Evolução do Engajamento</h3>
                                                <div className="chart-bars">
                                                    {(() => {
                                                        const periods = Object.entries(trendsData).filter(([period]) => period !== 'all');
                                                        const maxValue = Math.max(
                                                            ...periods.flatMap(([_, data]) => [
                                                                data.totalLikes || 0,
                                                                data.totalComments || 0,
                                                                data.totalShares || 0
                                                            ])
                                                        );
                                                        const scaleFactor = maxValue > 0 ? 100 / maxValue : 1;
                                                        
                                                        return periods.map(([period, data], index) => (
                                                            <div key={period} className="chart-bar-group">
                                                                <div className="bar-label">
                                                                    {period === '7d' ? 'Últimos 7 dias' : period === '30d' ? 'Últimos 30 dias' : period === '90d' ? 'Últimos 90 dias' : 'Total'}
                                                                </div>
                                                                <div className="chart-bar-container">
                                                                    <div 
                                                                        className="chart-bar likes-bar"
                                                                        style={{ 
                                                                            height: `${(data.totalLikes || 0) * scaleFactor}%`,
                                                                            backgroundColor: '#FF6B6B'
                                                                        }}
                                                                    ></div>
                                                                    <div 
                                                                        className="chart-bar comments-bar"
                                                                        style={{ 
                                                                            height: `${(data.totalComments || 0) * scaleFactor}%`,
                                                                            backgroundColor: '#4ECDC4'
                                                                        }}
                                                                    ></div>
                                                                    <div 
                                                                        className="chart-bar shares-bar"
                                                                        style={{ 
                                                                            height: `${(data.totalShares || 0) * scaleFactor}%`,
                                                                            backgroundColor: '#45B7D1'
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        ));
                                                    })()}
                                                </div>
                                                <div className="chart-legend">
                                                    <div className="legend-item">
                                                        <span className="legend-color" style={{backgroundColor: '#FF6B6B'}}></span>
                                                        <span>Curtidas</span>
                                                    </div>
                                                    <div className="legend-item">
                                                        <span className="legend-color" style={{backgroundColor: '#4ECDC4'}}></span>
                                                        <span>Comentários</span>
                                                    </div>
                                                    <div className="legend-item">
                                                        <span className="legend-color" style={{backgroundColor: '#45B7D1'}}></span>
                                                        <span>Compartilhamentos</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="chart-card">
                                                <h3>Performance por Período</h3>
                                                <div className="single-pie-chart">
                                                    <div className="pie-chart-container">
                                                        <div className="pie-chart">
                                                            {(() => {
                                                                const periods = Object.entries(trendsData).filter(([period]) => period !== 'all');
                                                                const totalPerformance = periods.reduce((sum, [_, data]) => sum + (data.performance || 0), 0);
                                                                let currentAngle = 0;
                                                                
                                                                return periods.map(([period, data], index) => {
                                                                    const percentage = totalPerformance > 0 ? (data.performance || 0) / totalPerformance : 0;
                                                                    const angle = percentage * 360;
                                                                    const startAngle = currentAngle;
                                                                    currentAngle += angle;
                                                                    
                                                                    return (
                                                                        <div
                                                                            key={period}
                                                                            className="pie-slice"
                                                                            style={{
                                                                                background: `conic-gradient(from ${startAngle}deg, ${index === 0 ? '#FF6B6B' : index === 1 ? '#4ECDC4' : index === 2 ? '#45B7D1' : '#8E59FF'} 0deg, ${index === 0 ? '#FF6B6B' : index === 1 ? '#4ECDC4' : index === 2 ? '#45B7D1' : '#8E59FF'} ${angle}deg, transparent ${angle}deg)`
                                                                            }}
                                                                        ></div>
                                                                    );
                                                                });
                                                            })()}
                                                        </div>
                                                        <div className="pie-center">
                                                            <div className="total-performance">
                                                                {(() => {
                                                                    const totalData = trendsData.all;
                                                                    return formatPercentage(totalData?.performance || 0);
                                                                })()}
                                                            </div>
                                                            <div className="total-label">Total</div>
                                                        </div>
                                                    </div>
                                                    <div className="pie-legend">
                                                        {Object.entries(trendsData).filter(([period]) => period !== 'all').map(([period, data], index) => (
                                                            <div key={period} className="legend-item">
                                                                <span 
                                                                    className="legend-color" 
                                                                    style={{
                                                                        backgroundColor: index === 0 ? '#FF6B6B' : index === 1 ? '#4ECDC4' : index === 2 ? '#45B7D1' : '#8E59FF'
                                                                    }}
                                                                ></span>
                                                                <span className="legend-label">
                                                                    {period === '7d' ? '7 Dias' : period === '30d' ? '30 Dias' : period === '90d' ? '90 Dias' : 'Total'}
                                                                </span>
                                                                <span className="legend-value">{formatPercentage(data.performance || 0)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                

                            </div>
                        </div>
                    </div>
                )}

                <div className="insights-section">
                    <h2>Insights</h2>
                    <div className="insights-grid">
                        <div className="insight-card">
                            <h4>Melhor Período</h4>
                            <p>Analise os horários com maior engajamento</p>
                        </div>
                        <div className="insight-card">
                            <h4>Conteúdo Mais Popular</h4>
                            <p>Identifique o que mais ressoa com sua audiência</p>
                        </div>
                        <div className="insight-card">
                            <h4>Crescimento</h4>
                            <p>Acompanhe sua evolução ao longo do tempo</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {renderSidebar()}
            <div className={`analytics-container${fromGrowthPilot ? ' growthpilot-font' : ''}${isSidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
                <div className={`analytics-content${fromGrowthPilot ? ' growthpilot-font' : ''}`}>
                    {renderDashboard()}
                </div>
            </div>
        </>
    );
};

export default Analytics; 